import moment from "moment-timezone";
import { booking_status } from "./mockup";

// Shared "find my booking by guest name" flow. Used by the PCI hub's
// checkin_by_name_component and by the Name tab of preCheckinCustomForm so
// both screens search, classify and lock out exactly the same way.

export const CHECKIN_BY_NAME_MAX_ATTEMPTS = 5;

export const CHECKED_OUT_STATUS_IDS = [
    booking_status.checked_out,
    booking_status.ready_to_pay_checkout,
    booking_status.checkout_completed,
    booking_status.check_out_hold,
    booking_status.check_out_payment,
    booking_status.check_out_completed_by_system,
];

export const BY_NAME_RESULT = Object.freeze({
    found: "found",
    locked_out: "locked_out",
    not_found: "not_found",
    multiple: "multiple",
    checked_in: "checked_in",
    checked_out: "checked_out",
    pci_completed: "pci_completed",
});

// Alert type + i18n key for every non-success result, so each screen only
// decides *how* to render the message, not *which* one.
export const BY_NAME_FEEDBACK = Object.freeze({
    [BY_NAME_RESULT.locked_out]: { type: "error", key: "checkin_by_name.error_locked_out" },
    [BY_NAME_RESULT.not_found]: { type: "error", key: "checkin_by_name.error_no_reservation" },
    [BY_NAME_RESULT.multiple]: { type: "error", key: "checkin_by_name.error_multiple_reservation" },
    [BY_NAME_RESULT.checked_in]: { type: "info", key: "checkin_by_name.info_already_checkedin" },
    [BY_NAME_RESULT.checked_out]: { type: "error", key: "checkin_by_name.error_already_checkedout" },
    [BY_NAME_RESULT.pci_completed]: { type: "info", key: "checkin_by_name.info_pci_already_completed" },
});

export default {
    data() {
        return {
            guest_name: "",
            byNameFailedAttempts: 0,
        };
    },

    computed: {
        isByNameLockedOut() {
            return this.byNameFailedAttempts >= CHECKIN_BY_NAME_MAX_ATTEMPTS;
        },
    },

    methods: {
        /**
         * Look up the booking for `guest_name` on `checkin_date` and classify the outcome.
         * `not_found` / `multiple` count toward the lockout; once locked out the API is
         * no longer called.
         *
         * @param {object} opts
         * @param {number} opts.facility_id
         * @param {string} opts.checkin_date          YYYY-MM-DD or YYYY/MM/DD
         * @param {boolean} [opts.preCheckinIsCompleted=false]
         *        When true a `pre_check_in` booking is reported as `pci_completed`
         *        instead of `found` (i.e. the guest has nothing left to do on this screen).
         * @returns {Promise<{ result: string, booking: object|null }>}
         */
        async searchBookingByName({ facility_id, checkin_date, preCheckinIsCompleted = false }) {
            if (this.isByNameLockedOut) return { result: BY_NAME_RESULT.locked_out, booking: null };

            const search = this.guest_name.trim();
            if (!search) return { result: BY_NAME_RESULT.not_found, booking: null };

            const response = await this.$lionheart
                .post("/facility-booking/list", {
                    facility_id,
                    mode: "name",
                    search,
                    checkin_date: moment(checkin_date, ["YYYY-MM-DD", "YYYY/MM/DD"]).format("YYYY-MM-DD"),
                })
                .then((r) => r.data)
                .catch((e) => this.catcher(e));

            const total = response?.total ?? 0;
            if (total < 1) return this.byNameFailedAttempt(BY_NAME_RESULT.not_found);
            if (total > 1) return this.byNameFailedAttempt(BY_NAME_RESULT.multiple);

            const booking = response.data[0];
            const status = booking.booking_status_id;

            if (status === booking_status.checked_in) return { result: BY_NAME_RESULT.checked_in, booking };
            if (CHECKED_OUT_STATUS_IDS.includes(status)) return { result: BY_NAME_RESULT.checked_out, booking };
            if (status === booking_status.pre_check_in && preCheckinIsCompleted) {
                return { result: BY_NAME_RESULT.pci_completed, booking };
            }

            return { result: BY_NAME_RESULT.found, booking };
        },

        byNameFailedAttempt(result) {
            this.byNameFailedAttempts++;
            return {
                result: this.isByNameLockedOut ? BY_NAME_RESULT.locked_out : result,
                booking: null,
            };
        },

        resetByNameState() {
            this.guest_name = "";
            this.byNameFailedAttempts = 0;
        },
    },
};
